import React from 'react'

import { RouteProps } from 'react-router'

import { ViewContainer } from './components'

export interface PrivacyPolicyProps extends RouteProps {
  termsOfUseUrl: string
}

export const PrivacyPolicy: React.FC<PrivacyPolicyProps> = ({
  termsOfUseUrl = 'https://centivo.com/terms/',
}) => (
  <ViewContainer>
    <div className="main-content template--privacy-policy">
      <p>
        We at Centivo Corporation (&quot;Centivo,&quot; &quot;we,&quot;
        &quot;us,&quot; or &quot;our&quot;) have created this privacy policy
        (this &quot;Privacy Policy&quot;) because we know that you care about
        how information you provide to us is used and shared. This Privacy
        Policy relates to the information collection and use practices of
        Centivo in connection with our website located at
        https://www.centivo.com/ (the “Site”), and our Services, which are made
        available to you through a variety of platforms, including, but not
        limited to:
      </p>
      <ul>
        <li>
          our Centivo Pre-Enrollment sites (“Pre-Enrollment sites”), which are
          made available through URLs that are unique to each of Centivo client
          formatted [clientname.centivo.com],
        </li>
        <li>
          our employer portal which is made available through
          <a
            href="https://employer.centivo.com/"
            target="_blank"
            rel="noreferrer"
          >
            Employer Portal
          </a>
          ,
        </li>
        <li>
          our provider portal which is made available through
          <a
            href="https://provider.centivo.com/"
            target="_blank"
            rel="noreferrer"
          >
            Provider Portal
          </a>
          ,
        </li>
        <li>
          and our member portal which is made available through
          <a href="https://my.centivo.com/" target="_blank" rel="noreferrer">
            https://my.centivo.com/
          </a>
        </li>
      </ul>
      <p>
        Member Portal, Employer Portal, and Provider Portal are collectively
        referred to herein as the “Centivo Portals.” The Centivo Portals are
        available via the URLs listed above and via our mobile app (“App”),
        which is accessible through tablets, smart phones, and other devices.
      </p>
      <h2>Description of Users and Acceptance of Terms</h2>
      <p>
        This Privacy Policy applies to visitors to the Site, who view only
        publicly available content (the “Visitors”), Authorized Users (as
        defined below) of our Customers and Members (as defined below).
      </p>
      <p>
        “Customers” means employers who have signed up to access and use our
        Services made available through the Employer Portal.
      </p>
      <p>
        “Authorized Users” means employees, contractors, providers, brokers,
        agents, or representatives of Customers who are authorized by Customer
        to access and use our Services made available through the Employer
        Portal or the Provider Portal.
      </p>
      <p>
        “Members” means (i) employees of Customers, together with their
        dependents who are enrolled in a health plan administered by Centivo and
        who have signed up to access and use our Services made available through
        the Member Portal; or (ii) individuals who have signed up to access and
        use our Services made available through the Member Portal.
      </p>
      <p>
        By accessing and/or using the Site, each Visitor is agreeing to the
        terms of this Privacy Policy and the
        <a href={termsOfUseUrl} target="_blank" rel="noreferrer">
          Terms of Use
        </a>
        .
      </p>
      <p>
        By clicking “I ACCEPT,” when signing up to access and use the Member
        Portal, each Member is agreeing to the terms of this Privacy Policy and
        the Centivo Portals
        <a href={termsOfUseUrl} target="_blank" rel="noreferrer">
          Terms of Use
        </a>
        .
      </p>
      <p>
        By signing up, accessing, and/or using the Services through the Provider
        Portal or the Employer Portal, each Authorized User is agreeing to the
        terms of this Privacy Policy and the Centivo Portals
        <a href={termsOfUseUrl} target="_blank" rel="noreferrer">
          Terms of Use
        </a>
        .
      </p>
      <p>
        Capitalized terms not defined in this Privacy Policy shall have the
        meaning set forth in our
        <a href={termsOfUseUrl} target="_blank" rel="noreferrer">
          Terms of Use
        </a>
        (when such term concerns Visitors) or the Centivo Portals
        <a href={termsOfUseUrl} target="_blank" rel="noreferrer">
          Terms of Use
        </a>
        (when such term concerns Customers, Members and Authorized Users).
      </p>
      <h2>1. The Information We Collect and/or Receive</h2>
      <p>
        In the course of operating the Site, the Centivo Portals, the Centivo
        Pre-Enrollment sites and/or providing the Services, Centivo will
        collect, store, use, transmit, and/or receive the following types of
        information. You authorize us to collect and/or receive such
        information.
      </p>
      <h3>Personal Information</h3>
      <p>
        If you would like to sign up for an account, you will need to provide
        your name, date of birth, e- mail address, phone number, zip code, and
        Member ID (if applicable). Centivo will use this information to verify
        that you are employed by an eligible employer in order to qualify for
        benefits.
      </p>
      <p>
        In order for us to set up an account for you on the Employer Portal or
        the Provider Portal, as applicable, you will need to provide your name,
        e-mail address, and Tax ID/NPI number (only for providers).
      </p>
      <p>
        If you contact us via the Contact Us page on our Site, you will need to
        provide your name, e- mail address, phone number and company name (if
        any).
      </p>
      <p>
        If you sign up to receive e-mails from Centivo, you will need to provide
        your e-mail address.
      </p>
      <p>
        All information we collect and/or receive under this section is
        collectively called &quot;Personal Information.&quot;
      </p>
      <h3>Member Information</h3>
      <p>
        We collect Member Information (as defined below) from the primary
        Member&apos;s employer who is our Customer, Member&apos;s healthcare
        providers, and other third-party vendors (e.g., pharmacy benefits
        manager) who provide services to Customer and/or Centivo. Member
        Information includes information about the Member and the Member&apos;s
        dependents, including but not limited to, name, e-mail address, date of
        birth, gender, and postal address (“Member Information”). Member
        Information is used to provide the Services, provide support, and to
        operate, maintain and improve the Services.
      </p>
      <h3>Health Risk Assessment Information</h3>
      <p>
        Members who sign up to use the Member Portal will also be asked to
        provide us with background health information that relates to their
        past, present, or future physical or mental health or condition,
        including but not limited to, their current health condition, how often
        they see a doctor, and whether they have any chronic health conditions
        (collectively, &quot;Health Risk Assessment Information&quot;).
      </p>
      <h3>Customer Data</h3>
      <p>
        In the course of using our Services, Customers provide us data
        (“Customer Data”). We use Customer Data in accordance with the terms and
        conditions of that certain master services agreement between the
        applicable Customer and Centivo.
      </p>
      <h3>Geolocational Information</h3>
      <p>
        In order to provide certain features and functionality of the App, we
        may, with your consent, automatically collect geolocational information
        from your mobile device, your wireless carrier, or certain third-party
        service providers (&quot;Geolocational Information&quot;). Collection of
        such Geolocational Information occurs only when the App is running on
        your mobile device. You may decline to allow us to collect such
        Geolocational Information, in which case Centivo will not be able to
        provide certain features of the App to you.
      </p>
      <h3>Other Information</h3>
      <p>
        In addition to the information noted above, we may collect additional
        information (collectively, the &quot;Other Information&quot;). Such
        Other Information may include:
      </p>
      <ol>
        <li>
          <b>From Your Activity.</b> Information that we automatically collect
          when you use the Site, the Centivo Portals or the Centivo
          Pre-Enrollment sites including, without limitation:
          <ul>
            <li>
              IP addresses, browser type and language, referring and exit pages
              and URLs, date and time, amount of time spent on particular pages,
              what sections of the Site, Centivo Portals or Centivo
              Pre-Enrollment sites you visit, etc.;
            </li>
            <li>
              Information about a mobile device, including universally unique ID
              (&quot;UUID&quot;), App type and version (e.g., iOS or Android),
              carrier and country location, hardware and processor information
              (storage, chip speed, camera resolution, NFC enabled, and network
              type (Wi-Fi, 2G, 3G, 4G)); and
            </li>
            <li>
              Activity and usage information occurring via the Site, Centivo
              Portals or Centivo Pre-Enrollment sites including tagging data,
              favorites, preferences, session lengths and similar data.
            </li>
          </ul>
        </li>
        <li>
          <b>From Cookies.</b> Information that we collect using
          &quot;cookie&quot; and “non-cookie” technology. Cookies are small
          packets of data that a website stores on your computer&apos;s or
          mobile device&apos;s hard drive so that your computer will
          &quot;remember&quot; information about your visit. We may use session
          cookies (which expire once you close your web browser) and persistent
          cookies (which stay on your computer until you delete them) and
          third-party cookies (which you can enable or disable) to help us
          collect Other Information to enhance your experience using the Site,
          the Centivo Portals and the Centivo Pre- Enrollment sites and to
          market to you. If you do not want us to place a cookie on your hard
          drive, you may be able to turn that feature off on your computer or
          mobile device. Please consult your Internet browser&apos;s
          documentation for information on how to do this and how to delete
          persistent cookies. However, if you decide not to accept cookies from
          us, the Site, Centivo Portals and the Centivo Pre-Enrollment sites may
          not function properly.
        </li>
        <li>
          <p>
            .<b>Third-Party Analytics.</b> We may use third-party analytics
            services (such as Google Analytics) to evaluate your use of the
            Site, the Centivo Portals and the Centivo Pre- Enrollment sites and
            compile reports on activity, collect demographic data, analyze
            performance metrics, and collect and evaluate other information
            relating to the Site, and the Centivo Portals and the Centivo
            Enrollment sites and mobile and Internet usage. These third parties
            use cookies and other technologies to help analyze and provide us
            the data. By accessing and/or using the Site, the Centivo Portals
            and/or the Centivo Pre- Enrollment site, you consent to the
            processing of data about you by these analytics providers in the
            manner and for the purposes set out in this Privacy Policy.
          </p>
          <p>
            For more information on these third parties, including how to opt
            out from certain data collection, please visit the sites below.
            Please be advised that if you opt out of any service, you may not be
            able to use the full functionality of the Site and the Centivo
            Portals.
          </p>
          <p>
            For Google Analytics, please visit
            <a
              href="https://www.google.com/analytics"
              target="_blank"
              rel="noreferrer"
            >
              https://www.google.com/analytics
            </a>
          </p>
          <p>
            For HubSpot, please visit
            <a
              href="https://legal.hubspot.com/privacy-
      policy?_ga=2.47047975.1682543742.1559741131-867457408.1559741131"
              target="_blank"
              rel="noreferrer"
            >
              https://legal.hubspot.com/privacy-
              policy?_ga=2.47047975.1682543742.1559741131-867457408.1559741131
            </a>
          </p>
          <p>
            For NextRoll (aka RollWorks), please visit
            <a
              href="https://www.nextroll.com/privacy"
              target="_blank"
              rel="noreferrer"
            >
              https://www.nextroll.com/privacy
            </a>
            .
          </p>
          <p>
            Our partners may use non-cookie technologies that may not be
            impacted by browser settings that block cookies. Your browser may
            not permit you to block such technologies. For this reason, you can
            use the following third-party tools to decline the collection and
            use of information for the purpose of serving you interest based
            advertising.
          </p>
          <ul>
            <li>
              The NAI&apos;s opt-out platform:
              http://www.networkadvertising.org/choices/
            </li>
            <li>
              The EDAA&apos;s opt-out platform:
              http://www.youronlinechoices.com/
            </li>
            <li>
              The DAA&apos;s opt-out platform:
              http://optout.aboutads.info/?c=2&lang=EN
            </li>
          </ul>
        </li>
      </ol>
      <h2>2. Text Messages</h2>
      <p>
        You may opt-in to receive SMS text messages from us relating to the
        Centivo Portals and Services. By opting-in to receive SMS text messages
        from us, you acknowledge that messaging and data rates may apply
        depending on your phone service provider. You may opt-out of receiving
        SMS text messages at any time by texting STOP in response to the text
        messages you receive or by changing your notification and communication
        preferences in the App. However, by opting out of receiving text
        messages, you may not be able to use full functionality of the Centivo
        Portals.
      </p>
      <h2>3. How We Use and Share Your Information</h2>
      <p>
        You authorize us to use the Personal Information, Member Information,
        Geolocational Information, Health Risk Assessment Information, and Other
        Information (collectively, the &quot;Information&quot;) to provide the
        Site, the Centivo Portals, Centivo Pre-Enrollment Sites, and the
        Services to you, solicit your feedback, inform you about our products
        and services and those of our third-party marketing partners, and to
        improve the Site, the Centivo Portals, and the Services. Also, we may
        use and share the Information as described below.
      </p>
      <ul>
        <li>
          <b>Agents, Providers and Related Third Parties.</b> We may engage
          other companies and individuals to perform certain business-related
          functions on our behalf. Examples may include providing technical
          assistance, customer service, and marketing assistance. These other
          companies will have access to the Information only as necessary to
          perform their functions and to the extent permitted by law. We may
          also share your Information with any of our parent companies,
          subsidiaries, or other companies under common control with us.
        </li>
        <li>
          <b>Aggregated Information.</b> In an ongoing effort to better
          understand users of the Site, Centivo Portals, Centivo Pre-Enrollment
          sites and the Services, we may analyze the Information in aggregate
          form in order to operate, maintain, manage, and improve the Site, the
          Centivo Portals, the Centivo Enrollment Sites and the Services. This
          aggregate information does not identify you personally. We may use
          this aggregate information for marketing purposes. We may share this
          aggregate information with our affiliates, agents, business and
          business partners, and other third parties. We may also disclose
          aggregated user statistics in order to describe the Site, the Centivo
          Portals, the Centivo Pre-Enrollment site and the Services to current
          and prospective business partners and to other third parties for other
          lawful purposes.
        </li>
        <li>
          <b>Business Transfers.</b> As we develop our businesses, we might sell
          or buy businesses or assets. In the event of a corporate sale, merger,
          reorganization, sale of assets, dissolution, or similar event, the
          Information may be part of the transferred assets.
        </li>
        <li>
          <b>Legal Requirements.</b> To the extent permitted by law, we may also
          disclose the Information: (i) when required by law, court order, or
          other government or law enforcement authority or regulatory agency; or
          (ii) whenever we believe that disclosing such Information is necessary
          or advisable, for example, to protect the rights, property, or safety
          of Centivo or others.
        </li>
      </ul>
      <h2>
        4. Accessing and Modifying Personal Information and Communication
        Preferences
      </h2>
      <p>
        If you have signed-up to use the Services, you may access, review, and
        make changes to your Personal Information by following the instructions
        found on the Centivo Portals. In addition, you may manage your receipt
        of marketing and non-transactional communications by clicking on the
        &quot;unsubscribe&quot; link located on the bottom of any Centivo
        marketing e-mail. You cannot opt out of receiving transactional e-mails
        related to your account. We will use commercially reasonable efforts to
        process such requests in a timely manner. You should be aware, however,
        that it is not always possible to completely remove or modify
        information in our subscription databases.
      </p>
      <h2>5. How We Protect the Information</h2>
      <p>
        We take commercially reasonable steps to protect the Information from
        loss, misuse, and unauthorized access, disclosure, alteration, or
        destruction. Please understand, however, that no security system is
        impenetrable. We cannot guarantee the security of our databases, nor can
        we guarantee that the Information that you supply will not be
        intercepted while being transmitted to and from us over the Internet. In
        particular, e-mail sent to or from the Site or the Centivo Portals or
        the Centivo Pre-Enrollment sites may not be secure, and you should
        therefore take special care in deciding what information you send to us
        via e-mail.
      </p>
      <h2>6. California Residents</h2>
      <p>
        In addition, Centivo does not monitor, recognize, or honor any opt-out
        or do not track mechanisms, including general web browser &quot;Do Not
        Track&quot; settings and/or signals.
      </p>
      <h2>7. How We Protect the Information</h2>
      <p>
        The Site, the Centivo Portals, the Centivo Pre-Enrollment sites, the
        Services and their servers are operated in the United States. Please be
        aware that your Information, including your Personal Information, may be
        transferred to, processed, maintained, and used on computers, servers,
        and systems located outside of your state, province, country, or other
        governmental jurisdiction where the privacy laws may not be as
        protective as those in your jurisdiction. If you are located outside the
        United States and choose to use the Site, the Centivo Portals, Centivo
        Pre-Enrollment sites, and/or the Services, you do so at your own risk.
      </p>
      <h2>8. Children</h2>
      <p>
        We do not knowingly collect Personal Information from children under the
        age of 13 through the Site, the Centivo Portals, the Centivo
        Pre-Enrollment sites, and/or the Services. If you are under 13, please
        do not give us any Personal Information. We encourage parents and legal
        guardians to monitor their children&apos;s Internet usage and to help
        enforce our Privacy Policy by instructing their children to never
        provide Personal Information through the Site, the Centivo Portals, the
        Centivo Pre-Enrollment sites, and/or the Services without their
        permission. If you have reason to believe that a child under the age of
        13 has provided Personal Information to us, please contact us, and we
        will endeavor to delete that information from our databases.
      </p>
      <h2> 9. External Websites</h2>
      <p>
        The Site may contain links to third-party websites (&quot;External
        Sites&quot;). We do not endorse such External Sites and we are not
        responsible for the privacy practices of such External Sites. Please
        refer to the privacy policies of those External Sites for more
        information on how the operators of those sites collect and use your
        personal information.
      </p>
      <h2>10. Changes to This Privacy Policy</h2>
      <p>
        This Privacy Policy is effective as of the date stated at the top of
        this Privacy Policy. We may change this Privacy Policy from time to
        time, and will post any changes on the Site, the Centivo Portals or the
        Centivo Pre-Enrollment sites as soon as they go into effect. By
        accessing and/or using the Site, Centivo Portals or the Centivo
        Pre-Enrollment sites after we make any such changes to this Privacy
        Policy, you are deemed to have accepted such changes. Please refer back
        to this Privacy Policy on a regular basis.
      </p>
      <h2>11. How to Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy, please e-mail
        Centivo&apos;s Privacy Officer at
        <a href="mailto:privacy@centivo.com" target="_blank" rel="noreferrer">
          privacy@centivo.com
        </a>
        , with &quot;PRIVACY POLICY&quot; in the subject line, or mail us at the
        following address:
      </p>
      <p>
        Centivo Privacy Officer, 77 Goodell Street, 5th Floor, Buffalo, NY 14203
      </p>
      <h2>Description of Users and Acceptance of Terms</h2>
    </div>
  </ViewContainer>
)
